import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { setShowBusinessDeactivationDialog } from "src/redux/slices/AuthSlice";
import { useMutation } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import toast from "react-hot-toast";

const BusinessDeactivationDialog = () => {
  const dispatch = useDispatch();
  const showBusinessDeactivationDialog = useSelector(
    (state) => state.auth.showBusinessDeactivationDialog
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    dispatch(
      setShowBusinessDeactivationDialog({
        show: false,
        businessId: null,
      })
    );
  };

  // Mutation to send reactivation request
  const sendReactivationRequestMutation = useMutation({
    mutationFn: async ({ businessId, message }) => {
      const res = await axiosInstance.post(
        ApiEndPoints.BUSINESS.requestReactivation(businessId),
        message ? { message } : {}
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data?.message || "reactivation request has been sent!");
      handleClose();
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          "failed to send reactivation request. please try again."
      );
    },
    onSettled: () => {
      setIsSubmitting(false);
    },
  });

  const handleSendRequest = () => {
    if (!showBusinessDeactivationDialog.businessId) {
      toast.error("business id not found");
      return;
    }

    setIsSubmitting(true);
    // Send reactivation request with optional message
    sendReactivationRequestMutation.mutate({
      businessId: showBusinessDeactivationDialog.businessId,
      message: "Please reactivate my business account", // Optional message
    });
  };

  return (
    <Dialog
      open={showBusinessDeactivationDialog.show}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 1400,
      }}
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: "#fff",
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          position: "relative",
        }}
      >
        {/* Close button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 3,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.9)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Content */}
        <Box sx={{ p: 4, pt: 5 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "#6C00E6",
              mb: 3,
              textTransform: "lowercase",
            }}
          >
            account deactivated!
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#333",
              mb: 4,
              lineHeight: 1.6,
            }}
          >
            your account is currently deactivated. to request reactivation, please
            contact our support team via email. our team will review your request
            and respond accordingly.
          </Typography>

          <Button
            variant="contained"
            fullWidth
            onClick={handleSendRequest}
            disabled={isSubmitting}
            sx={{
              py: 1.5,
              borderRadius: 2,
              fontWeight: 600,
              fontSize: "1rem",
              background: "#6C00E6",
              color: "#fff",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": {
                background: "#5500b3",
              },
              "&:disabled": {
                background: "#ccc",
                color: "#fff",
              },
            }}
          >
            {isSubmitting ? (
              <Box display="flex" alignItems="center" gap={2}>
                <CircularProgress size={20} color="inherit" />
                sending request...
              </Box>
            ) : (
              "send request"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default BusinessDeactivationDialog;

